/**
 * SAM-1298 — Support › Business Requests (canvas S1, table of C6 + chips). One inbox for the requests a business
 * sends: Business, Service, Employee (carnet) and Ad (Event keeps its own review in v1). The tenant admin and Xhinola see every type; the tenant
 * Front Desk sees only Employee. Rows open the detail (C7), where the request is approved or denied.
 * New file under the BaseUI freeze (SAM-175): Tailwind + tokens.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Inbox, Search } from 'lucide-react';
import type { MRT_ColumnDef } from 'mantine-react-table';
import { MantineDataTable } from '../../components/MantineDataTable';
import { AccessDenied } from '../../components/AccessDenied';
import { PhoneListState } from '../../components/phone/PhoneCards';
import { PhonePage, PhoneRowList, PhoneSearch, PhoneSelect, PhoneTitleRow, type PhoneRow } from '../../components/phone/PhoneListParts';
import { StatusChip } from '../BusinessPage/ActivityControls';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useIsPhone } from '../../hooks/useIsPhone';
import { businessRequestsApi } from '../../api/businessRequestsApi';
import {
  TYPE_LABEL, businessRequestKeys, chipCounts, dayOf, filterRows, groupNamer, requestRows, statusView, visibleTypes,
  type Chip, type InboxRow,
} from './businessRequestsLogic';

const EstateTag: React.FC = () => (
  <span className="ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[var(--info-bg,#ebf1f5)] text-[var(--tenant-primary,#005488)]">Estate</span>
);

export const BusinessRequests: React.FC = () => {
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const permissions = useUserPermissions();
  const types = visibleTypes(permissions);
  const [query, setQuery] = useState('');
  const [chip, setChip] = useState<Chip>('all');

  // One inbox: the server sends each role its part (the tenant Front Desk gets the carnets only), newest first.
  const requests = useQuery({
    queryKey: businessRequestKeys.list,
    queryFn: () => businessRequestsApi.list(),
    enabled: !permissions.isLoading && types.length > 0,
  });
  // Estate requests carry the estate group's id; its name comes from /options. Not needed by the Front Desk.
  const options = useQuery({
    queryKey: businessRequestKeys.options,
    queryFn: businessRequestsApi.options,
    enabled: !permissions.isLoading && permissions.isAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const all = requestRows((requests.data ?? []).filter((r) => types.includes(r.type)), groupNamer(options.data));
  const searched = filterRows(all, 'all', query);
  const counts = chipCounts(searched);
  const rows = filterRows(searched, chip, '');
  const chips: Chip[] = types.length > 1 ? ['all', ...types] : types;
  const activeChip: Chip = chips.includes(chip) ? chip : chips[0] ?? 'all';
  const loading = requests.isLoading;
  const failed = requests.isError;

  const columns = useMemo<MRT_ColumnDef<InboxRow>[]>(() => [
    {
      id: 'type', header: 'TYPE', size: 110, accessorFn: (r) => TYPE_LABEL[r?.type] ?? '',
      Cell: ({ row }) => <span className="whitespace-nowrap">{TYPE_LABEL[row.original.type]}{row.original.estate && <EstateTag />}</span>,
    },
    { accessorKey: 'business', header: 'BUSINESS', size: 150, Cell: ({ row }) => row.original.business || '—' },
    { accessorKey: 'requestedBy', header: 'REQUESTED BY', size: 160, Cell: ({ row }) => row.original.requestedBy || '—' },
    { accessorKey: 'summary', header: 'REQUEST', size: 260 },
    { id: 'sent', header: 'SENT', size: 120, accessorFn: (r) => r?.sentAt ?? '', Cell: ({ row }) => dayOf(row.original.sentAt) || '—' },
    {
      id: 'status', header: 'REQUEST STATUS', size: 130, accessorFn: (r) => statusView(r?.uiStatus).label,
      Cell: ({ row }) => { const v = statusView(row.original.uiStatus); return <StatusChip tone={v.tone} label={v.label} />; },
    },
  ], []);

  if (permissions.isLoading) return <div className="p-6 text-sm text-[var(--ink-500)]">Loading…</div>;
  if (!types.length) return <AccessDenied />;

  const chipLabel = (c: Chip) => (c === 'all' ? 'All' : TYPE_LABEL[c]);
  const emptyText = query || activeChip !== 'all' ? 'No matching requests' : 'No requests yet';

  if (isPhone) {
    const phoneRows: PhoneRow[] = rows.map((r) => ({
      key: r.key,
      when: dayOf(r.sentAt),
      status: statusView(r.uiStatus),
      name: r.summary,
      sub: [`${TYPE_LABEL[r.type]}${r.estate ? ' · Estate' : ''}`, r.business].filter(Boolean).join(' · '),
      extra: r.requestedBy || undefined,
      open: () => navigate(r.href),
    }));
    return (
      <PhonePage>
        <PhoneTitleRow title="Business Requests" />
        <PhoneSearch placeholder="Search by business or person…" value={query} onChange={setQuery} />
        {chips.length > 1 && (
          <PhoneSelect
            ariaLabel="Filter by type"
            value={activeChip}
            display={`${chipLabel(activeChip)} · ${counts[activeChip]}`}
            groups={[{ options: chips.map((c) => ({ id: c, label: chipLabel(c) })) }]}
            onChange={(id) => setChip(id as Chip)}
          />
        )}
        <PhoneListState loading={loading} failed={failed} empty={!loading && !failed && phoneRows.length === 0} emptyText={emptyText} />
        {!loading && phoneRows.length > 0 && <PhoneRowList rows={phoneRows} />}
      </PhonePage>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-white">
      <div className="px-6 pt-5">
        <h1 className="m-0 text-[28px] font-bold text-[var(--ink-900)]">Business Requests</h1>
      </div>
      <div className="flex items-center gap-3 px-6 py-3 flex-wrap">
        <label className="flex items-center gap-2 flex-1 max-w-[320px] rounded-[20px] bg-[#EBEBEB] border border-solid border-[#EBEBEB] px-3.5 py-[7px]">
          <Search size={16} color="#9CA3AF" aria-hidden />
          <input
            aria-label="Search by business or person"
            className="border-0 outline-none bg-transparent text-[14px] text-[#374151] flex-1 w-full font-[inherit]"
            placeholder="Search by business or person…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        {chips.length > 1 && (
          <div role="group" aria-label="Filter by type" className="flex gap-1.5 flex-wrap">
            {chips.map((c) => {
              const on = activeChip === c;
              return (
                <button
                  key={c}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setChip(c)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-solid text-[13px] font-semibold cursor-pointer font-[inherit]"
                  style={{
                    borderColor: on ? 'var(--tenant-primary, #111827)' : '#E5E7EB',
                    backgroundColor: on ? 'color-mix(in srgb, var(--tenant-primary, #111827) 8%, white)' : '#fff',
                    color: on ? 'var(--tenant-primary, #111827)' : '#374151',
                  }}
                >
                  {chipLabel(c)} <span className="text-[11px] font-semibold text-[#6B7280]">{counts[c]}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="flex-1 overflow-auto px-6 pb-6">
        {failed && !loading ? (
          <div role="alert" className="py-16 text-center text-[14px] text-[var(--ink-500)]">The requests could not be loaded. Try again in a moment.</div>
        ) : !loading && rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[14px] text-[#6B7280]">
            <Inbox size={40} color="#D1D5DB" className="mb-3" aria-hidden />
            {emptyText}
          </div>
        ) : (
          <MantineDataTable columns={columns} data={rows} loading={loading} enablePagination enableSorting onRowClick={(r) => navigate((r as InboxRow).href)} />
        )}
      </div>
    </div>
  );
};

export default BusinessRequests;
